<template>
  <div>
    <div class="setup-ct">
      <form-section
        :title="$t('CampaignForm_Setup_Details')"
        :separator="true"
        :is-changed="model.diff && model.diff.includes('details')"
      >
        <el-row>
          <el-col :span="12">
            <rest-form-item :label="$t('CampaignForm_Setup_Name')" prop="title">
              <el-input v-model="model.title" data-qa="campaign-title-input" />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
      <form-section
        :title="$t('CampaignForm_Setup_EngagementCategory')"
        :description="$t('CampaginForm_Setup_EngagementCategoryDesc')"
        :separator="true"
        :is-changed="model.diff && model.diff.includes('engagement_category')"
        v-if="displayCategorySelector"
      >
        <el-row type="flex" align="middle">
          <el-col :span="12">
            <rest-form-item prop="category_id">
              <el-select
                filterable
                remote
                :remote-method="loadCategories"
                :loading="categoryLoading"
                v-model="model.category_id"
                clearable
                @visible-change="handleCategoryDropdownVisibleChange"
                @clear="engagementCategoryExpanded = false"
                data-qa="category-dropdown"
              >
                <el-option
                  v-for="category in visibleCategoryOptions"
                  :key="category.id"
                  :value="category.id"
                  :label="category.title"
                />
              </el-select>
              <template #label>
                <span>{{ $t('CampaignForm_Setup_Category') }}</span>
                <form-item-i-tooltip>
                  <template #content>
                    <a @click="viewAllCategories" class="tooltip-link">
                      {{ $t('CampaignForm_Setup_SeeAllCategories') }}
                    </a>
                  </template>
                </form-item-i-tooltip>
              </template>
            </rest-form-item>
          </el-col>
          <el-col :span="12">
            <el-button
              class="expand-category-button"
              type="text"
              :disabled="!model.category_id || !selectedCategory.id"
              @click="expandCategorySettings"
            >
              {{
                engagementCategoryExpanded
                  ? $t('CampaignForm_Setup_Category_CollapseSettings')
                  : $t('CampaignForm_Setup_Category_ExpandSettings')
              }}
              <i
                class="el-icon-arrow-down"
                v-if="!engagementCategoryExpanded"
              />
              <i class="el-icon-arrow-up" v-if="engagementCategoryExpanded" />
            </el-button>
          </el-col>
        </el-row>
        <el-row v-if="engagementCategoryExpanded" class="separator-top">
          <el-col :span="24">
            <div class="section-title">
              {{ $t('CampaignForm_Setup_Category_ChosenCategorySettings') }}
              <form-item-i-tooltip
                v-if="model.type === campaignTypeEnum.TYPE_WORKFLOW"
              >
                <template #content>
                  {{ $t('CampaignForm_Setup_Category_WorkflowExplainer') }}
                </template>
              </form-item-i-tooltip>
            </div>
            <p class="section-text">
              {{
                model.type === campaignTypeEnum.TYPE_WORKFLOW
                  ? $t('CampaignForm_Setup_Category_AllowJourneyEntry')
                  : $t('CampaignForm_Setup_Category_AllowSendingTo')
              }}
              <el-tag type="info" size="mini">{{ selectedSegmentName }}</el-tag>
            </p>
            <p
              class="section-text"
              v-if="model.type !== campaignTypeEnum.TYPE_WORKFLOW"
            >
              {{ $t('CampaignForm_Setup_Category_IgnoreSubscription') }}
              {{
                selectedCategory.ignore_subscription
                  ? $t('CampaignForm_Setup_Category_True')
                  : $t('CampaignForm_Setup_Category_False')
              }}
            </p>
            <p
              class="section-text"
              v-if="
                isNationalExclusionAvailable &&
                  model.type !== campaignTypeEnum.TYPE_WORKFLOW
              "
            >
              {{ $t('CampaignForm_Setup_Category_Use_National_Exclusion') }}
              {{
                isExclusionAvailableForCategory
                  ? $t('CampaignForm_Setup_Category_True')
                  : $t('CampaignForm_Setup_Category_False')
              }}
            </p>
            <p
              class="section-text"
              v-if="
                isNationalExclusionAvailable &&
                  model.type !== campaignTypeEnum.TYPE_WORKFLOW
              "
            >
              {{ $t('CampaignForm_Setup_Category_National_Exclusion_Check') }}
              {{ getNationalExclusionCheck }}
            </p>
            <p
              class="section-text"
              v-if="model.type !== campaignTypeEnum.TYPE_WORKFLOW"
            >
              {{ $t('CampaignForm_Setup_Category_NoCollapsing') }}
              {{
                selectedCategory.collapse
                  ? $t('CampaignForm_Setup_Category_True')
                  : $t('CampaignForm_Setup_Category_False')
              }}
            </p>
            <div class="section-sub-title">
              {{ $t('CampaignForm_Setup_Category_Global_Exclusion') }}
              <span>{{ hasGlobalExclusion }}</span>
            </div>
            <div class="section-desc">
              {{ $t('Engagement_Category_Exclusion_Description') }}
            </div>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_Exclude_All_Categories') }}
              {{
                selectedCategory.global_exclusion
                  ? $t('CampaignForm_Setup_Category_True')
                  : $t('CampaignForm_Setup_Category_False')
              }}
            </p>
            <p class="section-text exclusion-list-ct">
              <span>{{
                $t('Engagement_Category_Limit_By_Engagement_Category')
              }}</span>
              <span class="exclusion-list">
                <el-tag
                  v-for="(category, index) in categoryExcludeList"
                  :key="index"
                  type="info"
                  size="mini"
                  style="margin-right: 5px;"
                >
                  {{ category }}
                </el-tag>
              </span>
            </p>
            <p class="section-text">
              {{
                $t(
                  'CampaignForm_Setup_Category_Global_Exclusion_TimeFrame_Amount'
                )
              }}
              {{
                selectedCategory.exclusion_amount ||
                  $t(
                    'CampaignForm_Setup_Category_National_Exclusion_Check_None'
                  )
              }}
            </p>
            <p class="section-text">
              {{
                $t(
                  'CampaignForm_Setup_Category_Global_Exclusion_TimeFrame_Measure'
                )
              }}
              {{
                selectedCategory.exclusion_measure ||
                  $t(
                    'CampaignForm_Setup_Category_National_Exclusion_Check_None'
                  )
              }}
            </p>
            <div class="section-sub-title">
              {{ $t('CampaignForm_Setup_Category_FrequencyCapping') }}
              <span>
                {{
                  selectedCategoryHasFrequencyCapping
                    ? $t('CampaignForm_Setup_Category_True')
                    : $t('CampaignForm_Setup_Category_False')
                }}
              </span>
            </div>
            <div class="section-desc">
              {{ $t('CampaignForm_Setup_Category_FrequencyCapping_Desc') }}
            </div>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_MaxPerDay') }}
              {{ selectedCategory.max_per_day || '∞' }}
            </p>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_MaxPerWeek') }}
              {{ selectedCategory.max_per_week || '∞' }}
            </p>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_MaxPerMonth') }}
              {{ selectedCategory.max_per_month || '∞' }}
            </p>
            <p v-if="$hasFeature('lifetimeSends')" class="section-text">
              {{ $t('CampaignForm_Setup_Category_MaxLifetimeSends') }}
              {{ selectedCategory.max_lifetime_sends || '∞' }}
            </p>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_LimitPerChannel') }}
              {{
                selectedCategory.apply_across_channels
                  ? $t('CampaignForm_Setup_Category_True')
                  : $t('CampaignForm_Setup_Category_False')
              }}
            </p>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_Cooldown') }}
              {{ selectedCategory.cooldown_amount || '∞' }}
            </p>
            <p class="section-text">
              {{ $t('CampaignForm_Setup_Category_CooldownMeasure') }}
              {{
                selectedCategory.cooldown_measure ||
                  $t('CampaignForm_Setup_Category_CooldownMeasure_None')
              }}
            </p>
            <div class="section-title">
              <el-button type="primary" plain @click="viewAllCategories">
                {{ $t('CampaignForm_Setup_Category_ViewAll') }}
                <i class="el-icon-view el-icon--right" />
              </el-button>
            </div>
          </el-col>
        </el-row>
      </form-section>
      <form-section-checkbox
        v-if="
          $hasFeature('subscriptionPreferences') &&
            model.type === campaignTypeEnum.TYPE_NOTIFICATION
        "
        :title="$t('CampaignForm_Setup_Consent_Preference')"
        :description="$t('CampaignForm_Setup_Consent_Preference_Description')"
        v-model="model.consent_preference"
        data-qa="consent-preference-checkbox"
      >
        <el-row>
          <el-col :span="12">
            <consent-preference-setup v-model="model" />
          </el-col>
          <el-col :span="12"></el-col>
        </el-row>
      </form-section-checkbox>

      <div
        style="margin-bottom: 20px"
        v-if="model.type !== campaignTypeEnum.TYPE_WORKFLOW"
      >
        <channels-select v-model="model" />
      </div>

      <form-section-checkbox
        v-if="hasPromotions"
        :title="$t('CampaignForm_Setup_Promotions_Checkbox_Title')"
        :description="$t('CampaignForm_Setup_Promotions_Checkbox_Desc')"
        v-model="model.promotion"
        :is-changed="model.diff && model.diff.includes('promotions')"
        :show-expander="!model.ab"
      >
        <promotion-setup v-model="model" />
      </form-section-checkbox>
      <form-section-checkbox
        v-model="model.ab"
        :title="$t('CampaignForm_Setup_Variants_Checkbox_Title')"
        :description="$t('CampaignForm_Setup_Variants_Checkbox_Desc')"
        :is-changed="model.diff && model.diff.includes('variants')"
        :disabled="!allowVariantsChange"
        data-qa="variant-setup-ab-section"
      >
        <variant-setup v-model="model" />
      </form-section-checkbox>
      <form-section-checkbox
        v-if="model.type !== campaignTypeEnum.TYPE_WORKFLOW"
        :title="$t('CampaignForm_Setup_MultiLanguage_Checkbox_Title')"
        :description="$t('CampaignForm_Setup_MultiLanguage_Checkbox_Desc')"
        v-model="model.languages_multi"
        :disabled="!!variantWinner"
        :is-changed="model.diff && model.diff.includes('multilanguage')"
      >
        <multi-language-selector v-model="model" />
      </form-section-checkbox>
      <form-section-checkbox
        v-if="hasContentFetch"
        :title="$t('CampaignForm_Setup_DynamicContent_Checkbox_Title')"
        :description="$t('CampaignForm_Setup_DynamicContent_Checkbox_Desc')"
        v-model="model.content_fetch"
        :is-changed="model.diff && model.diff.includes('content_fetch')"
      >
        <fetch-dynamic-content v-model="model" />
      </form-section-checkbox>
      <form-section-checkbox
        :title="$t('CampaignForm_Setup_GoalConversion_Checkbox_Title')"
        :description="
          $t('CampaignForm_Setup_GoalConversion_Checkbox_Description')
        "
        v-model="model.conversions"
        :is-changed="model.diff && model.diff.includes('conversions')"
        id="goal_conversion_section"
        :class="{ highlight: highlightGoalConversion }"
      >
        <goal-conversion :model="model" />
      </form-section-checkbox>
      <form-section
        v-if="model.type === campaignTypeEnum.TYPE_WORKFLOW"
        :title="$t('CampaignForm_Setup_CancelJourney_Title')"
        :description="$t('CampaignForm_Setup_CancelJourney_Desc')"
        :separator="true"
        :is-changed="model.diff && model.diff.includes('earlyCancelEvent')"
      >
        <early-cancel-event-selector :model="model" />
      </form-section>
      <form-section-checkbox
        v-if="hasVoucherCodes"
        :title="$t('CampaignForm_Setup_VoucherCodes_Checkbox_Title')"
        :description="$t('CampaignForm_Setup_VoucherCodes_Checkbox_Desc')"
        v-model="model.voucher_codes_enabled"
        :is-changed="model.diff && model.diff.includes('voucher_codes')"
      >
        <voucher-codes-setup v-model="model" />
      </form-section-checkbox>
    </div>
    <div class="footer">
      <div class="footer-ct">
        <el-button type="primary" @click="nextStep" data-qa="next-step-btn">
          {{ $t('CampaignForm_Setup_NextButton_Title') }}
          <i class="el-icon-arrow-right el-icon-right" />
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import FetchDynamicContent from '@/modules/campaign-manage/components/setup/FetchDynamicContent'
import ChannelsSelect from '@/modules/campaign-manage/components/setup/ChannelsSelect'
import { EventBus } from '@/core/EventBus'
import GoalConversion from '@/modules/campaign-manage/components/setup/GoalConversion'
import MultiLanguageSelector from '@/modules/campaign-manage/components/setup/MultiLanguageSelector'
import VariantSetup from '@/modules/campaign-manage/components/setup/VariantSetup'
import FormSectionCheckbox from '@/common/components/form/FormSectionCheckbox'
import PromotionSetup from '@/modules/campaign-manage/components/setup/PromotionSetup'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { $hasFeature } from '@/core/utils/VueAcl'
import { channelLabels } from '@/modules/campaign-manage/enum/Labels'
import FormItemITooltip from '@/common/components/form/FormItemITooltip'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import RestApi from '@/core/services/RestApi'
import {
  nationalExclusionTypeLabels,
  nationalExclusionTypes
} from '@/common/enum/CategoryEnums'
import { mapGetters } from 'vuex'
import EarlyCancelEventSelector from '@/modules/campaign-manage/components/setup/EarlyCancelEventSelector.vue'
import VoucherCodesSetup from '@/modules/campaign-manage/components/setup/VoucherCodesSetup.vue'
import ConsentPreferenceSetup from '@/modules/campaign-manage/components/setup/ConsentPreferenceSetup'

export default {
  name: 'StepSetup',
  components: {
    ConsentPreferenceSetup,
    EarlyCancelEventSelector,
    FormSectionCheckbox,
    PromotionSetup,
    VariantSetup,
    GoalConversion,
    MultiLanguageSelector,
    ChannelsSelect,
    FetchDynamicContent,
    RestFormItem,
    FormSection,
    FormItemITooltip,
    VoucherCodesSetup
  },
  props: {
    model: {
      type: Object
    }
  },
  data() {
    return {
      channelLabels,
      campaignTypeEnum,
      campaignTriggerTypes,
      nationalExclusionTypeLabels,
      nationalExclusionTypes,
      categories: [],
      categoryOptions: [],
      categoryLoading: false,
      selectedCategoryDetails: {},
      engagementCategoryExpanded: false,
      segments: [],
      highlightGoalConversion: false
    }
  },
  watch: {
    'model.category_id'(newId) {
      this.engagementCategoryExpanded = false
      this.loadSelectedCategory(newId)
    }
  },
  mounted() {
    if (window.localStorage.getItem('goToGoal')) {
      this.goToGoalConversion()
    }
    this.loadCategories()
    if (this.model.category_id) {
      this.loadSelectedCategory(this.model.category_id)
    }
    new RestApiPaginator(this.segmentsFetchEndpoint)
      .fetchAllPages()
      .then(data => {
        this.segments = data
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
      })
  },
  computed: {
    ...mapGetters(['isBetssonAvailable', 'isOasisAvailable']),
    variantWinner() {
      return this.model.variants.find(variant => variant.winner)
    },
    hasPromotions() {
      return (
        $hasFeature('hasPromotions') &&
        this.model.type === campaignTypeEnum.TYPE_NOTIFICATION
      )
    },
    hasContentFetch() {
      return $hasFeature('contentFetch')
    },
    hasVoucherCodes() {
      return (
        this.model.trigger === campaignTriggerTypes.TRIGGER_NONE &&
        ($hasFeature('allowVoucherCodes') || this.model.voucher_codes_enabled)
      )
    },
    segmentsFetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'segments',
        params: {
          'per-page': 50
        }
      })
    },
    activeChannelsCount() {
      return Object.keys(this.channelLabels).filter(
        item => this.model.hasOwnProperty(item) && !!this.model[item]
      ).length
    },
    displayCategorySelector() {
      return (
        !$hasFeature('locationsPriorities') &&
        ($hasFeature('categoriesRequired') ||
          this.categories.length > 0 ||
          !!this.model.category_id)
      )
    },
    visibleCategoryOptions() {
      const options = this.categoryOptions
      const selected = this.selectedCategoryDetails
      if (
        selected &&
        selected.id &&
        String(selected.id) === String(this.model.category_id) &&
        !options.some(option => String(option.id) === String(selected.id))
      ) {
        return [{ id: selected.id, title: selected.title }, ...options]
      }
      return options
    },
    selectedCategory() {
      if (
        this.selectedCategoryDetails &&
        String(this.selectedCategoryDetails.id) ===
          String(this.model.category_id)
      ) {
        return this.selectedCategoryDetails
      }
      return {}
    },
    selectedSegment() {
      if (!this.selectedCategory.segment_id) {
        return null
      }
      return (
        (this.segments || []).find(
          segment => segment.id === this.selectedCategory.segment_id
        ) || {}
      )
    },
    selectedSegmentName() {
      if (this.selectedSegment) {
        return this.selectedSegment.title
      }
      return this.$t('CampaignForm_Setup_Category_Everybody')
    },
    selectedCategoryHasFrequencyCapping() {
      return (
        this.selectedCategory.max_per_day ||
        this.selectedCategory.max_per_week ||
        this.selectedCategory.max_per_month ||
        this.selectedCategory.max_lifetime_sends ||
        this.selectedCategory.cooldown_amount
      )
    },
    hasGlobalExclusion() {
      if (
        (this.selectedCategory.global_exclusion ||
          this.selectedCategory.exclusion_categories) &&
        this.selectedCategory.exclusion_amount &&
        this.selectedCategory.exclusion_measure
      ) {
        return this.$t('CampaignForm_Setup_Category_True')
      }
      return this.$t('CampaignForm_Setup_Category_False')
    },
    isNationalExclusionAvailable() {
      return this.isBetssonEnabled || this.isOasisEnabled
    },
    isBetssonEnabled() {
      return this.isBetssonAvailable && !!$hasFeature('betssonIntegration')
    },
    isOasisEnabled() {
      return this.isOasisAvailable && !!$hasFeature('oasisIntegration')
    },
    categoryExcludeList() {
      const excludeList = (
        this.selectedCategory.exclusion_categories || []
      ).map(category => category.title)

      return excludeList.length
        ? excludeList
        : [this.$t('CampaignForm_Setup_Category_National_Exclusion_Check_None')]
    },
    getNationalExclusionCheck() {
      if (
        this.selectedCategory.use_national_exclusion &&
        this.selectedCategory.national_exclusion_check !== null &&
        this.isExclusionAvailableForCategory
      ) {
        let exclusionType = this.$t(
          // eslint-disable-next-line standard/computed-property-even-spacing
          nationalExclusionTypeLabels[
            this.selectedCategory.national_exclusion_check
          ]
        )
        if (this.isOasisEnabled && this.isBetssonEnabled) {
          let groupName = this.$t(
            'Engagement_Category_National_Exclusion_GroupTechsson'
          )
          if (
            [
              nationalExclusionTypes.marketing_oasis,
              nationalExclusionTypes.marketing_bonus_oasis
            ].includes(this.selectedCategory.national_exclusion_check)
          ) {
            groupName = this.$t(
              'Engagement_Category_National_Exclusion_GroupOasis'
            )
          }

          exclusionType = exclusionType + ' (' + groupName + ')'
        }

        return exclusionType
      }
      return this.$t(
        'CampaignForm_Setup_Category_National_Exclusion_Check_None'
      )
    },
    isExclusionAvailableForCategory() {
      let oasisTypes = [
        nationalExclusionTypes.marketing_oasis,
        nationalExclusionTypes.marketing_bonus_oasis
      ]

      if (oasisTypes.includes(this.selectedCategory.national_exclusion_check)) {
        return this.isOasisEnabled
      }

      return this.isBetssonEnabled
    },
    allowVariantsChange() {
      return (
        !this.model.id || this.model.draft || this.model.is_campaign_template
      )
    }
  },
  methods: {
    handleCategoryDropdownVisibleChange(open) {
      if (open) {
        this.loadCategories()
      }
    },
    loadCategories(rawQuery = '') {
      const query = typeof rawQuery === 'string' ? rawQuery.trim() : ''

      if (!query && this.categories.length) {
        this.categoryOptions = this.categories
        return Promise.resolve(this.categories)
      }

      const params = {
        page: 1,
        'per-page': 50,
        sort: 'title',
        fields: 'id,title'
      }

      if (query) {
        params.title = query
      }

      this.categoryLoading = true

      return RestApi(
        projectEndpoint({
          method: 'get',
          url: 'categories',
          params
        })
      )
        .then(({ data }) => {
          if (!query) {
            this.categories = data
          }

          this.categoryOptions = data
          return data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.categoryLoading = false
        })
    },
    loadSelectedCategory(categoryId) {
      this.selectedCategoryDetails = {}

      if (!categoryId) {
        return Promise.resolve()
      }

      return RestApi(
        projectEndpoint({
          method: 'get',
          url: `categories/${categoryId}`,
          params: {
            expand: 'exclusion_categories'
          }
        })
      )
        .then(({ data }) => {
          this.selectedCategoryDetails = data
          return data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    goToGoalConversion() {
      this.$nextTick(() => {
        document.getElementById('goal_conversion_section').scrollIntoView()

        this.highlightGoalConversion = true

        setTimeout(() => {
          this.highlightGoalConversion = false
        }, 1000)

        window.localStorage.removeItem('goToGoal')
      })
    },
    nextStep() {
      EventBus.$emit('campaign-create-next-step')
    },
    viewAllCategories() {
      let routeData = this.$router.resolve(
        projectRoute({ name: 'EngagementCategoriesTab' })
      )
      window.open(routeData.href, '_blank')
    },
    expandCategorySettings() {
      this.engagementCategoryExpanded = !this.engagementCategoryExpanded
    }
  }
}
</script>

<style scoped>
>>> .el-radio {
  height: 68px !important;
}

>>> .el-form-item {
  margin-bottom: 0;
}

.exclusion-list-ct {
  display: flex;
  column-gap: 3px;
}

.exclusion-list {
  max-width: 700px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expand-category-button {
  float: right;
  color: #1d87c8;
  font-weight: 600;
  font-size: 14px;
  &.is-disabled {
    color: #c0c4cc;
  }
}

.setup-ct {
  padding-bottom: 60px;
}

.footer {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}

.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

.tooltip-link {
  color: white;
  cursor: pointer;
  text-decoration: underline;
}

.separator-top {
  margin-top: 20px;
  border-top: 1px solid #dbdee7;
  padding-top: 20px;
}

.section-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #4b515f;
  &:not(:first-child) {
    padding-top: 30px;
  }
}

.section-sub-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: #4b515f;
  padding-top: 20px;
}

.section-title span,
.section-sub-title span {
  font-weight: normal;
}
.section-desc {
  font-size: 12px;
  line-height: 18px;
  color: #5c6374;
}

.section-text {
  padding-top: 10px;
  line-height: 22px;
  font-size: 14px;
  color: #4b515f;
}

.highlight {
  background-color: #cbe6ef;
  -webkit-transition: background-color 500ms linear;
  -moz-transition: background-color 500ms linear;
  -o-transition: background-color 500ms linear;
  -ms-transition: background-color 500ms linear;
  transition: background-color 500ms linear;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/pages/StepSetup.vue